/**
 * Custom hook for managing PDF bookmarks
 *
 * Extracted from PDFViewerModal.tsx
 */

import { useState, useEffect, useCallback } from 'react';
import type { Bookmark } from '../types/pdf-viewer.types';

export interface UsePDFBookmarksProps {
  documentId?: string;
  pageNumber: number;
  onPageChange?: (page: number) => void;
}

export interface UsePDFBookmarksReturn {
  bookmarks: Bookmark[];
  showBookmarksPanel: boolean;
  setShowBookmarksPanel: (show: boolean) => void;
  addBookmark: () => void;
  removeBookmark: (id: string) => void;
  goToBookmark: (bookmark: Bookmark) => void;
}

/**
 * Manages PDF bookmarks state and operations
 */
export function usePDFBookmarks({
  documentId,
  pageNumber,
  onPageChange,
}: UsePDFBookmarksProps): UsePDFBookmarksReturn {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [showBookmarksPanel, setShowBookmarksPanel] = useState(false);

  // Load bookmarks from localStorage
  useEffect(() => {
    if (documentId) {
      try {
        const savedBookmarks = localStorage.getItem(`pdf-bookmarks-${documentId}`);
        if (savedBookmarks) {
          setBookmarks(JSON.parse(savedBookmarks));
        }
      } catch (error) {
        console.error('Error loading bookmarks:', error);
      }
    }
  }, [documentId]);

  // Save bookmarks to localStorage
  const saveBookmarks = useCallback((newBookmarks: Bookmark[]) => {
    if (documentId) {
      try {
        localStorage.setItem(`pdf-bookmarks-${documentId}`, JSON.stringify(newBookmarks));
      } catch (error) {
        console.error('Error saving bookmarks:', error);
      }
    }
  }, [documentId]);

  const addBookmark = useCallback(() => {
    const newBookmark: Bookmark = {
      id: `bookmark-${Date.now()}`,
      page: pageNumber,
      label: `Página ${pageNumber}`,
      createdAt: Date.now()
    };
    const newBookmarks = [...bookmarks, newBookmark];
    setBookmarks(newBookmarks);
    saveBookmarks(newBookmarks);
  }, [pageNumber, bookmarks, saveBookmarks]);

  const removeBookmark = useCallback((id: string) => {
    const newBookmarks = bookmarks.filter(b => b.id !== id);
    setBookmarks(newBookmarks);
    saveBookmarks(newBookmarks);
  }, [bookmarks, saveBookmarks]);

  const goToBookmark = useCallback((bookmark: Bookmark) => {
    onPageChange?.(bookmark.page);
  }, [onPageChange]);

  return {
    bookmarks,
    showBookmarksPanel,
    setShowBookmarksPanel,
    addBookmark,
    removeBookmark,
    goToBookmark,
  };
}
